import { format, parseISO } from "date-fns";

import { formatDollars, formatTransactionAmount } from "@/shared/utils/formatters";
import {
  BRAND_GREEN,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BORDER_COLOR,
  loadLogoAsDataUrl,
  type RGBTuple,
} from "@/shared/utils/pdfBranding";

const SUMMARY_BG: RGBTuple = [250, 250, 249];
import { getCategoryConfig } from "@/pages/postloss/estate-inventory/constants/transactionCategories";

import type { PlaidTransaction } from "@/shared/hooks/useTransactions";

interface GenerateTransactionsPdfParams {
  transactions: PlaidTransaction[];
  accountName: string;
  estateDisplayName: string;
  balance: number;
  moneyIn: number;
  moneyOut: number;
}

const MARGIN = 20;
const LINE_HEIGHT = 6;

export async function generateTransactionsPdf({
  transactions,
  accountName,
  estateDisplayName,
  balance,
  moneyIn,
  moneyOut,
}: GenerateTransactionsPdfParams) {
  const { default: jsPDF } = await import("jspdf");
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const contentWidth = pageWidth - 2 * MARGIN;
  let y = MARGIN;

  const checkPage = (needed: number) => {
    if (y + needed > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const logoDataUrl = await loadLogoAsDataUrl();
  const logoHeight = 8;
  const logoWidth = logoHeight * (1701 / 441);
  doc.addImage(logoDataUrl, "PNG", MARGIN, y, logoWidth, logoHeight);
  y += logoHeight + 8;

  doc.setDrawColor(...BORDER_COLOR);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 10;

  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TEXT_PRIMARY);
  doc.text("Estate Account Transactions", MARGIN, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(...TEXT_SECONDARY);
  doc.text(`${accountName} · ${estateDisplayName}`, MARGIN, y + 4);
  y += 6;
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}`,
    MARGIN,
    y + 4,
  );
  y += 14;

  doc.setDrawColor(...BORDER_COLOR);
  doc.setFillColor(...SUMMARY_BG);
  doc.roundedRect(MARGIN, y, contentWidth, 28, 2, 2, "FD");

  const colWidth = contentWidth / 3;
  const summaryItems = [
    { label: "Net Activity", value: formatDollars({ amount: balance, rounding: "whole" }) },
    { label: "Money In", value: `+${formatDollars({ amount: moneyIn, rounding: "whole" })}` },
    { label: "Money Out", value: formatDollars({ amount: moneyOut, rounding: "whole" }) },
  ];

  summaryItems.forEach((item, i) => {
    const colX = MARGIN + colWidth * i + 8;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(item.label, colX, y + 10);
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...TEXT_PRIMARY);
    doc.text(item.value, colX, y + 20);
  });

  y += 38;

  // Table header
  const dateCol = MARGIN;
  const descCol = MARGIN + 30;
  const catCol = MARGIN + 95;
  const amtCol = pageWidth - MARGIN;

  checkPage(14);
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...TEXT_SECONDARY);
  doc.text("Date", dateCol, y);
  doc.text("Description", descCol, y);
  doc.text("Category", catCol, y);
  doc.text("Amount", amtCol, y, { align: "right" });
  y += 2;
  doc.setDrawColor(...BRAND_GREEN);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 5;

  // Transaction rows
  for (const tx of transactions) {
    checkPage(8);

    const dateStr = format(parseISO(tx.date), "MMM d, yyyy");
    const categoryConfig = getCategoryConfig(tx.category, tx.is_inflow, tx.description, tx.original_description);
    const categoryLabel = categoryConfig?.label ?? "";
    const amountStr = formatTransactionAmount(tx.amount, tx.is_inflow);

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(dateStr, dateCol, y);

    doc.setTextColor(...TEXT_PRIMARY);
    const descText: string[] = doc.splitTextToSize(tx.description, 60);
    doc.text(descText[0], descCol, y);

    doc.setTextColor(...TEXT_SECONDARY);
    doc.text(categoryLabel, catCol, y);

    doc.setFont("helvetica", "bold");
    const amountColor = tx.is_inflow ? BRAND_GREEN : TEXT_PRIMARY;
    doc.setTextColor(...amountColor);
    doc.text(amountStr, amtCol, y, { align: "right" });

    y += LINE_HEIGHT;

    doc.setDrawColor(...BORDER_COLOR);
    doc.setLineWidth(0.2);
    doc.line(MARGIN, y, pageWidth - MARGIN, y);
    y += 3;
  }

  const footerY = pageHeight - 10;
  const totalPages = doc.getNumberOfPages();
  for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
    doc.setPage(pageNum);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...TEXT_SECONDARY);
    doc.text("Generated by Elayne - elayne.co", MARGIN, footerY);
    doc.text(`Page ${pageNum} of ${totalPages}`, pageWidth - MARGIN, footerY, { align: "right" });
  }

  doc.save("Estate_Account_Transactions.pdf");
}
